import React, { useState, useEffect } from 'react';
import { deleteSiteUser, updateSite, deleteSite, fetchSiteUsers, updateSiteUser, createSiteUser, fetchSiteUserPermissions, createSiteUserPermission, deleteSiteUserPermission } from '../../services/sites';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { Modal } from '../Common/Modal';
import { Clipboard, Lock, Trash2, LogOut, ChevronDown, ChevronRight, Plus, Settings } from 'lucide-react';
import { PERMISSIONS } from '../../utils/constants';


const Site = ({ site, onUpdate, editEnabled }) => {
  const { theme } = useTheme();
  const { user: currentUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(site.name);
  const [showSecret, setShowSecret] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showUsers, setShowUsers] = useState(false);
  const [users, setUsers] = useState([]);
  const [editingRole, setEditingRole] = useState(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('User');
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedUserPermissions, setSelectedUserPermissions] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  const isOwner = site.role === 'Owner';
  const canManage = isOwner || site.role === 'Admin';

  const showNotification = (message) => {
    setNotification(message);
    setTimeout(() => setNotification(null), 3000);
  };

  const loadUsers = async () => {
    try {
      const response = await fetchSiteUsers(site.site_id);
      // Filter out current user
      setUsers(response.filter(user => user.user_id !== currentUser.userId));
    } catch (error) {
      console.error('Error loading users:', error);
      showNotification('Failed to load users');
    }
  };

  const loadUserPermissions = async (userId) => {
    try {
      const permissions = await fetchSiteUserPermissions(site.site_id, userId);
      setSelectedUserPermissions(permissions);
    } catch (error) {
      console.error('Error loading user permissions:', error);
      showNotification('Failed to load user permissions');
    }
  };

  const handlePermissionChange = async (permission) => {
    try {
      if (selectedUserPermissions.includes(permission)) {
        await deleteSiteUserPermission(selectedUserId, site.site_id, permission);
        setSelectedUserPermissions(prev => prev.filter(p => p !== permission));
      } else {
        await createSiteUserPermission(selectedUserId, site.site_id, permission);
        setSelectedUserPermissions(prev => [...prev, permission]);
      }
      showNotification('Permissions updated successfully');
    } catch (error) {
      console.error('Error updating permissions:', error);
      showNotification('Failed to update permissions');
    }
  };

  const openPermissionsModal = (userId) => {
    setSelectedUserId(userId);
    loadUserPermissions(userId);
    setShowPermissionsModal(true);
  };

  useEffect(() => {
    if (showUsers && canManage) {
      loadUsers();
    }
  }, [showUsers]);

  const handleUpdateName = async () => {
    if (!isOwner) return;
    try {
      await updateSite(site.site_id, name);
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating site name:', error);
    }
  };

  const handleToggleDisable = async () => {
    if (!isOwner) return;
    try {
      await updateSite(site.site_id, null, null, null, !site.is_disabled);
      onUpdate();
    } catch (error) {
      console.error('Error toggling site status:', error);
    }
  };

  const generateRandomSecret = () => {
    return Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2);
  };

  const handleRegenerateSecret = async () => {
    if (!isOwner) return;
    try {
      const newSecret = generateRandomSecret();
      await updateSite(site.site_id, null, null, newSecret);
      onUpdate();
      setShowRegenerateModal(false);
      showNotification('Site secret regenerated successfully');
    } catch (error) {
      console.error('Error regenerating secret:', error);
      showNotification('Failed to regenerate site secret');
    }
  };

  const handleLeaveSite = async () => {
    try {
      await deleteSiteUser(currentUser.userId, site.site_id);
      onUpdate();
      setShowLeaveModal(false);
      showNotification('Successfully left the site');
    } catch (error) {
      console.error('Error leaving site:', error);
      showNotification('Failed to leave site');
    }
  };

  const handleDeleteSite = async () => {
    try {
      await deleteSite(site.site_id);
      onUpdate();
      setShowDeleteModal(false);
      showNotification('Site deleted successfully');
    } catch (error) {
      console.error('Error deleting site:', error);
      showNotification('Failed to delete site');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await deleteSiteUser(userToDelete, site.site_id);
      showNotification('User removed successfully');
      setShowDeleteUserModal(false);
      setUserToDelete(null);
      loadUsers();
    } catch (error) {
      console.error('Error removing user:', error);
      showNotification('Failed to remove user');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await updateSiteUser(userId, site.site_id, newRole);
      showNotification('Role updated successfully');
      setEditingRole(null);
      loadUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      showNotification('Failed to update role');
    }
  };

  const handleAddUser = async () => {
    try {
      await createSiteUser(site.site_id, newUserEmail, newUserRole);
      showNotification('User added successfully');
      setShowAddUserModal(false);
      setNewUserEmail('');
      setNewUserRole('User');
      loadUsers();
    } catch (error) {
      console.error('Error adding user:', error);
      showNotification('Failed to add user');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showNotification('Copied to clipboard!');
  };

  return (
    <div className={`p-4 rounded-lg border ${
      theme === 'light' ? 'border-gray-200 bg-white' : 'border-gray-700 bg-gray-800'
    } relative`}>
      {site.is_locked && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm rounded-lg flex items-center justify-center">
          <div className="text-center text-white space-y-2">
            <Lock className="w-8 h-8 mx-auto" />
            <p className="font-medium">Site Locked</p>
            {site.reason && <p className="text-sm opacity-80">{site.reason}</p>}
          </div>
        </div>
      )}
      
      <div className="space-y-4">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            {isEditing ? (
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`px-2 py-1 rounded border ${
                  theme === 'light' 
                    ? 'border-gray-300 bg-white text-gray-900' 
                    : 'border-gray-700 bg-gray-800 text-gray-100'
                }`}
              />
            ) : (
              <h3 className={`text-lg font-semibold ${theme === 'light' ? 'text-gray-900' : 'text-gray-100'}`}>
                {site.name}
              </h3>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            {isOwner && (
              <div className="flex gap-2">
                {isEditing ? (
                  <button
                    onClick={handleUpdateName}
                    className="px-2 py-1 text-xs rounded-full border-purple-500/30 text-purple-500 bg-purple-500/10 hover:bg-purple-500/20"
                    title="Save site name changes"
                  >
                    Save
                  </button>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-2 py-1 text-xs rounded-full border-purple-500/30 text-purple-500 bg-purple-500/10 hover:bg-purple-500/20"
                    title="Edit site name"
                  >
                    Edit
                  </button>
                )}
              </div>
            )}
            
            {isOwner && (
              <button
                onClick={handleToggleDisable}
                className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none ${
                  !site.is_disabled ? 'bg-green-500' : 'bg-red-500'
                }`}
                title={site.is_disabled ? 'Enable site' : 'Disable site'}
              >
                <span className="sr-only">Enable site</span>
                <span
                  className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                    !site.is_disabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            )}

            {!isOwner ? (
              <button
                onClick={() => setShowLeaveModal(true)}
                className="p-2 rounded-full hover:bg-red-100 dark:hover:bg-red-900/20"
                title="Leave Site"
              >
                <LogOut className="w-4 h-4 text-red-500" />
              </button>
            ) : (
              <button
                onClick={() => setShowDeleteModal(true)}
                className="p-2 rounded-full hover:bg-red-100 dark:hover:bg-red-900/20"
                title="Delete Site"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            )}
          </div>
        </div>

        <div className={`text-sm ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`}>
          <p>Points: {site.point_count}</p>
          <p>Created: {new Date(site.created_at).toLocaleDateString()}</p>
          <p>Role: {site.role}</p>
          
          {canManage && (
            <div className="mt-2 space-y-2">
              <div className="flex items-center justify-between">
                <span>Site Key:</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono">{site.site_key}</span>
                  <button
                    onClick={() => site.site_key && copyToClipboard(site.site_key)}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="Copy site key to clipboard"
                  >
                    <Clipboard className="w-4 h-4 text-blue-500" />
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span>Site Secret:</span>
                <div className="flex items-center gap-2">
                  {showSecret ? (
                    <span className="font-mono">{site.site_secret}</span>
                  ) : (
                    <span>••••••••</span>
                  )}
                  <div className="flex gap-1">
                    <button
                      onClick={() => setShowSecret(!showSecret)}
                      className="px-2 py-1 text-xs rounded-full border-blue-500/30 text-blue-500 bg-blue-500/10 hover:bg-blue-500/20"
                      title={showSecret ? 'Hide site secret' : 'Show site secret'}
                    >
                      {showSecret ? 'Hide' : 'Show'}
                    </button>
                    {showSecret && (
                      <button
                        onClick={() => site.site_secret && copyToClipboard(site.site_secret)}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Copy site secret to clipboard"
                      >
                        <Clipboard className="w-4 h-4 text-blue-500" />
                      </button>
                    )}
                    {isOwner && (
                      <button
                        onClick={() => setShowRegenerateModal(true)}
                        className="px-2 py-1 text-xs rounded-full border-yellow-500/30 text-yellow-500 bg-yellow-500/10 hover:bg-yellow-500/20"
                        title="Generate new site secret"
                      >
                        Regenerate
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {canManage && (
            <div className="mt-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setShowUsers(!showUsers)}
                  className={`flex items-center gap-1 text-sm font-medium ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}`}
                >
                  {showUsers ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  Users
                </button>
                {showUsers && (
                  <button
                    onClick={() => setShowAddUserModal(true)}
                    className="flex items-center gap-1 px-2 py-1 text-xs rounded-full border-green-500/30 text-green-500 bg-green-500/10 hover:bg-green-500/20"
                  >
                    <Plus className="w-3 h-3" />
                    Add User
                  </button>
                )}
              </div>
              
              {showUsers && (
                <div className="mt-2 overflow-x-auto">
                  <table className={`min-w-full divide-y ${theme === 'light' ? 'divide-gray-200' : 'divide-gray-700'}`}>
                    <thead>
                      <tr>
                        <th className={`px-4 py-2 text-left text-xs font-medium ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>Email</th>
                        <th className={`px-4 py-2 text-left text-xs font-medium ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>Role</th>
                        <th className={`px-4 py-2 text-left text-xs font-medium ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${theme === 'light' ? 'divide-gray-200' : 'divide-gray-700'}`}>
                      {users.map((user) => (
                        <tr key={user.user_id}>
                          <td className={`px-4 py-2 text-sm ${theme === 'light' ? 'text-gray-900' : 'text-gray-100'}`}>{user.email}</td>
                          <td className="px-4 py-2">
                            {editingRole === user.user_id && isOwner ? (
                              <select
                                value={user.role}
                                onChange={(e) => handleUpdateRole(user.user_id, e.target.value)}
                                className={`text-sm rounded ${theme === 'light' ? 'border-gray-300 bg-white' : 'border-gray-700 bg-gray-800'}`}
                              >
                                {['Owner', 'Admin', 'User'].map((role) => (
                                  <option key={role} value={role}>{role}</option>
                                ))}
                              </select>
                            ) : (
                              <span 
                                className={`text-sm ${isOwner ? 'cursor-pointer hover:text-blue-500' : ''} ${theme === 'light' ? 'text-gray-900' : 'text-gray-100'}`}
                                onClick={() => isOwner && setEditingRole(user.user_id)}
                              >
                                {user.role}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              {user.role === 'User' && (
                                <button
                                  onClick={() => openPermissionsModal(user.user_id)}
                                  className="p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900/20"
                                  title="Edit permissions"
                                >
                                  <Settings className="w-4 h-4 text-blue-500" />
                                </button>
                              )}
                              {(isOwner || (site.role === 'Admin' && user.role === 'User')) && (
                                <button
                                  onClick={() => {
                                    setUserToDelete(user.user_id);
                                    setShowDeleteUserModal(true);
                                  }}
                                  className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20"
                                  title="Remove user"
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {notification && (
          <div className={`mt-4 p-3 rounded-lg ${
            notification.includes('error') || notification.includes('fail')
              ? 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
              : 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
          }`}>
            {notification}
          </div>
        )}
      </div>

      <Modal
        isOpen={showRegenerateModal}
        onClose={() => setShowRegenerateModal(false)}
        title="Regenerate Site Secret"
      >
        <div className="p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Are you sure you want to regenerate the site secret? This will invalidate the current secret and any applications using it will need to be updated.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowRegenerateModal(false)}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleRegenerateSecret}
              className="px-4 py-2 text-sm rounded-lg bg-yellow-500 text-white hover:bg-yellow-600"
            >
              Regenerate
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        title="Leave Site"
      >
        <div className="p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Are you sure you want to leave this site? You will lose access to all site features and data.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowLeaveModal(false)}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleLeaveSite}
              className="px-4 py-2 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600"
            >
              Leave Site
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Site"
      >
        <div className="p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Are you sure you want to delete this site? This action cannot be undone and all site data will be permanently lost.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteSite}
              className="px-4 py-2 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600"
            >
              Delete Site
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        title="Add User"
      >
        <div className="p-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                placeholder="user@example.com"
              />
            </div>
            {isOwner && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Role
                </label>
                <select
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                >
                  {['User', 'Admin', 'Owner'].map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => setShowAddUserModal(false)}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleAddUser}
              className="px-4 py-2 text-sm rounded-lg bg-green-500 text-white hover:bg-green-600"
            >
              Add User
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showPermissionsModal}
        onClose={() => setShowPermissionsModal(false)}
        title="Edit User Permissions"
      >
        <div className="p-4">
          <div className="space-y-4 max-h-[50vh] overflow-y-auto">
            {Object.entries(PERMISSIONS).map(([category, permissions]) => (
              <div key={category}>
                <h3 className="font-medium mb-2">{category}</h3>
                <div className="space-y-2">
                  {Object.entries(permissions).map(([key, permission]) => {
                    const isEditPermission = permission.startsWith('edit_');
                    const correspondingViewPermission = isEditPermission ? 
                      `view_${permission.slice(5)}` : null;
                    const hasEditPermission = isEditPermission ? 
                      selectedUserPermissions.includes(permission) : 
                      permission.startsWith('view_') && 
                      selectedUserPermissions.includes(`edit_${permission.slice(5)}`);
                    
                    return (
                      <label key={permission} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedUserPermissions.includes(permission)}
                          onChange={() => {
                            if (isEditPermission) {
                              // When enabling edit, also enable view
                              if (!selectedUserPermissions.includes(permission)) {
                                handlePermissionChange(correspondingViewPermission);
                              }
                              handlePermissionChange(permission);
                            } else if (!hasEditPermission) {
                              // Only allow changing view if edit is not enabled
                              handlePermissionChange(permission);
                            }
                          }}
                          disabled={hasEditPermission && !isEditPermission}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm">{permission}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setShowPermissionsModal(false)}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showDeleteUserModal}
        onClose={() => {
          setShowDeleteUserModal(false);
          setUserToDelete(null);
        }}
        title="Remove User"
      >
        <div className="p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Are you sure you want to remove this user from the site? They will lose access to all site features and data.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setShowDeleteUserModal(false);
                setUserToDelete(null);
              }}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteUser}
              className="px-4 py-2 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600"
            >
              Remove User
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Site;
